import {
  Box,
  Flex,
  Divider,
  Text,
  VStack,
  Button,
  InputGroup,
  InputRightElement,
  Input,
  useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

import { API_TYPE } from '../../constants/api';
import Footer from '../../components/Footer';

import { getRoomId } from '../../utils/home';
import { post } from '../../utils/api';
import { getGameRoomUrl } from '../../utils/getRoomUrl';
import logo from '../../assets/logo.svg';
import bgImg from '../../assets/homepageBg.svg';
import blurImg from '../../assets/blurBg.png';
import { borders } from '../../theme';
import CustomImage from '../../components/CustomImage';
import { useIsMobile } from '../../hooks/useMobile';
import HomeMobile from './mobile';

const HomePage = () => {
  const [inviteLink, setInviteLink] = useState('');
  const [isPasting, setIsPasting] = useState(false);

  const toast = useToast();
  const navigate = useNavigate();

  const { isMobile } = useIsMobile();
  const onStartNewGame = () => {
    navigate('/create-game');
  };

  const onPaste = async () => {
    try {
      setIsPasting(true);
      const [gameNetwork, gameContractAddress] = getRoomId(inviteLink);
      const res = await post(API_TYPE.GAME, '/verify_game', {
        gameNetwork,
        gameContractAddress,
      });
      if (res.status !== 200) {
        const errMess = await res.json();
        throw new Error(errMess);
      }
      navigate(getGameRoomUrl(gameNetwork, gameContractAddress));
    } catch (e: any) {
      console.error(e);
      toast({
        title: 'Invalid invite link',
        description: e?.message ?? e,
        status: 'error',
      });
    } finally {
      setIsPasting(false);
    }
  };

  if (isMobile) {
    return (
      <HomeMobile
        onStartNewGame={onStartNewGame}
        inviteLink={inviteLink}
        setInviteLink={setInviteLink}
        onPaste={onPaste}
        isPasting={isPasting}
      />
    );
  }

  return (
    <Flex
      flexDirection={'column'}
      px={10}
      pos={'relative'}
      overflow={'hidden'}
      w="100%"
      justifyContent={'center'}
    >
      <Flex
        flex={1}
        basis={{ base: '100%', lg: '50%' }}
        flexDir={{ base: 'column', lg: 'row' }}
        px={{ base: '0.625rem', lg: 4 }}
        gap={20}
      >
        <Box flexGrow={1} order={{ base: 2, lg: 1 }}>
          <Flex
            flexDir={'column'}
            h="100%"
            justifyContent={'center'}
            alignItems={'center'}
            pl={16}
            pos="relative"
          >
            <CustomImage
              src={bgImg}
              alt="background"
              h={'38rem'}
              pos="absolute"
            />
            <CustomImage src={logo} alt="logo" h={52} />

            <Text textStyle="h1" mt="2rem">
              TEXAS HOLD'EM IN 3 EASY STEPS
            </Text>
            <Text textStyle="p1" mt="1rem" w="100%" textAlign={'center'}>
              1. Create a game link
            </Text>
            <Text textStyle="p1" w="100%" textAlign={'center'}>
              2. Send the link to your friends
            </Text>
            <Text textStyle="p1" w="100%" textAlign={'center'}>
              3. Deposit your buy-in into the vault and starting playing.
            </Text>
          </Flex>
        </Box>

        <Box flexGrow={1} order={{ base: 1, lg: 2 }}>
          <Flex
            flexDir={'column'}
            h="100%"
            justifyContent={'center'}
            alignItems={'flex-start'}
          >
            <Box
              border={borders.normal}
              borderRadius={'30px'}
              p={{ base: 8, lg: 14 }}
              w={{ base: '90%', lg: '100%' }}
              maxW={{ base: 'unset', lg: '32rem' }}
              h={'fit-content'}
              bg={'gray1304'}
              backdropFilter={'blur(20px)'}
            >
              <Text textStyle={'h1'} textAlign={'center'}>
                Poker with Friends
                <br />
                Settled Onchain
              </Text>
              <VStack spacing={6} mt={'4rem'} w="100%">
                <Button
                  variant={'normal'}
                  h={'3.75rem'}
                  w="100%"
                  onClick={onStartNewGame}
                >
                  Start a New Game
                </Button>
                <Flex
                  gap={2}
                  position="relative"
                  py="4"
                  w="100%"
                  textStyle="p3"
                  alignItems={'center'}
                >
                  <Divider w="44%" borderColor={'whiteAlpha.400'} />
                  <Text>OR</Text>
                  <Divider w="44%" borderColor={'whiteAlpha.400'} />
                </Flex>
                <InputGroup>
                  <Input
                    textStyle={'p3'}
                    _placeholder={{
                      fontSize: 'md',
                      color: 'whiteAlpha.400',
                      lineHeight: '121%',
                      fontWeight: 'medium',
                    }}
                    pr="4.5rem"
                    border={borders.white20}
                    color="white"
                    placeholder="Enter Your Invite Link"
                    value={inviteLink}
                    onChange={e => {
                      setInviteLink(e.currentTarget.value);
                    }}
                    w={'100%'}
                    h={'3.1rem'}
                  />
                  <InputRightElement width="4.5rem" h="3.1rem" pr="0.5rem">
                    <Button
                      variant={'normal'}
                      borderRadius={'4px'}
                      h="2.1rem"
                      size="sm"
                      onClick={onPaste}
                      isLoading={isPasting}
                      isDisabled={!inviteLink}
                    >
                      Paste
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </VStack>
            </Box>
          </Flex>
        </Box>
      </Flex>

      <Footer />
      <CustomImage
        src={blurImg}
        alt="background"
        w={'90rem'}
        h={'64rem'}
        pos="absolute"
        top="50%"
        left="50%"
        transform={'translate(-50%, -50%)'}
        zIndex="-2"
      />
    </Flex>
  );
};

export default HomePage;
